import React, { useState, useEffect } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { Icon } from "@iconify/react/dist/iconify.js";

const ResetPasswordLayer = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');
  const API_BASE_URL = process.env.REACT_APP_BE_API_URL;

  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [tokenValid, setTokenValid] = useState(null);
  const [passwordChanged, setPasswordChanged] = useState(false);

  useEffect(() => {
    // Validate token on component mount
    const validateToken = async () => {
      if (!token) {
        setTokenValid(false);
        return;
      }
  
      try {
        setLoading(true);
        const response = await fetch(`${API_BASE_URL}/backend/users/validate-reset-token`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ token })
        });
  
        const data = await response.json();
  
        // Set state only, don't toast here
        setTokenValid(response.ok && data.status);
      } catch (error) {
        setTokenValid(false);
      } finally {
        setLoading(false);
      }
    };
  
    validateToken();
  }, [token, API_BASE_URL]);
  
  // This useEffect will toast only once when tokenValid becomes false
  useEffect(() => {
    if (tokenValid === false) {
      toast.error("Invalid or expired reset link");
    }
  }, [tokenValid]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!newPassword || !confirmPassword) {
      toast.error("Please fill in all fields");
      return;
    }
    
    if (newPassword !== confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/backend/users/change-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          token,
          new_password: newPassword,
          confirm_password: confirmPassword
        })
      });

      const data = await response.json();
      
      if (response.ok && data.status) {
        toast.success("Password changed successfully");
        setPasswordChanged(true);
        setTimeout(() => navigate('/sign-in'), 3000);
      } else {
        toast.error(data.message || "Failed to change password. Please try again.");
      }
    } catch (error) {
      toast.error("An error occurred. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (tokenValid === null) {
    return (
      <section className="auth-container">
        <div className="frosted-glass-card">
          <div className="card-content">
            <div className="flex justify-center">
              <span className="spinner" role="status" aria-hidden="true"></span>
            </div>
            <p className="text-center mt-4">Validating reset link...</p>
          </div>
        </div>
      </section>
    );
  }

  if (!tokenValid) {
    return (
      <section className="auth-container">
        <ToastContainer />
        <div className="frosted-glass-card">
          <div className="card-content">
            <Link to="/" className="logo-link">
              <div className="flex items-center">
                <img
                  src="assets/images/logo_fw.png"
                  alt="Logo"
                  className="logo-image"
                />
              </div>
            </Link>
            <h4 className="card-title">Invalid Reset Link</h4>
            <p className="card-subtitle text-red-500">
              The password reset link is invalid or has expired. Please request a new one.
            </p>
            
            <div className="mt-6">
              <Link 
                to="/forgot-password" 
                className="submit-button text-center block"
              >
                Request New Reset Link
              </Link>
            </div>

            <div className="auth-footer mt-40">
              <Link to="/sign-in" className="auth-footer-link text-success-700">
                Back to Sign In
              </Link>
            </div>
          </div>
        </div>
      </section>
    );
  }

  if (passwordChanged) {
    return (
      <section className="auth-container">
        <ToastContainer />
        <div className="frosted-glass-card">
          <div className="card-content">
            <Link to="/" className="logo-link">
              <div className="flex items-center">
                <img
                  src="assets/images/logo_fw.png"
                  alt="Logo"
                  className="logo-image"
                />
              </div>
            </Link>
            <h4 className="card-title">Password Changed</h4>
            <p className="card-subtitle text-green-500">
              Your password has been changed successfully. You will be redirected to the login page shortly.
            </p>
            
            <div className="auth-footer mt-6">
              <Link to="/sign-in" className="auth-footer-link">
                Go to Sign In Now
              </Link>
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="auth-container">
      <ToastContainer />
      
      <div className="frosted-glass-card">
        <div className="card-content">
          <Link to="/" className="logo-link">
            <div className="flex items-center">
              <img
                src="assets/images/logo_fw.png"
                alt="Logo"
                className="logo-image"
              />
            </div>
          </Link>
          <h4 className="card-title">Reset Password</h4>
          <p className="card-subtitle">
            Enter your new password below
          </p>

          <form onSubmit={handleSubmit} className="auth-form">
            <div className="form-field">
              <span className="form-icon pb-3">
                <Icon icon="solar:lock-password-bold" />
              </span>
              <input
                type="password"
                className="form-input"
                placeholder="New Password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
              />
            </div>

            <div className="form-field">
              <span className="form-icon pb-3">
                <Icon icon="solar:lock-password-bold" />
              </span>
              <input
                type="password"
                className="form-input"
                placeholder="Confirm New Password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
              />
            </div>
            
            <button
              type="submit"
              className="submit-button text-center"
              disabled={loading}
            >
              {loading ? (
                <span className="spinner" role="status" aria-hidden="true"></span>
              ) : (
                "Reset Password"
              )}
            </button>
          </form>

          <div className="auth-footer mt-40">
              <Link to="/sign-in" className="auth-footer-link text-success-700">
              Back to Sign In
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ResetPasswordLayer;